<template>
  <section class="schedule-section">
    <div class="schedule-section__heading">
      <h3 class="schedule-section__title">{{ title }}</h3>
      <a-radio-group class="schedule-section__type" :value="section.selected_type" @change="changeType">
        <a-radio value="weekdays">Weekdays</a-radio>
        <a-radio value="interval">Interval</a-radio>
      </a-radio-group>
    </div>

    <div v-if="section.selected_type === 'weekdays'" class="schedule-section__days">
      <schedule-timeline
        v-for="day in weekdays"
        :key="day.key"
        :title="day.label"
        :intervals="displayWeekdayIntervals(day.key)"
        @add="openEditor('weekdays', day.key, weekdayIntervals(day.key).length)"
        @edit="openWeekdayEditor"
        @clear="clearWeekdayPayload"
      />
    </div>

    <div v-else class="schedule-section__interval">
      <div class="schedule-section__interval-fields">
        <label class="schedule-section__field">
          <span>Working days</span>
          <a-input-number
            :min="1"
            :value="interval.working_days_amount"
            @change="updateIntervalField('working_days_amount', $event)"
          />
        </label>
        <label class="schedule-section__field">
          <span>Rest days</span>
          <a-input-number
            :min="0"
            :value="interval.rest_days_amount"
            @change="updateIntervalField('rest_days_amount', $event)"
          />
        </label>
        <label class="schedule-section__field">
          <span>Start date</span>
          <a-date-picker
            class="schedule-section__date"
            :value="intervalStartDate"
            format="MM/DD/YYYY"
            placeholder="Select date"
            @change="changeIntervalStartDate"
          />
        </label>
        <a-checkbox
          class="schedule-section__checkbox"
          :checked="interval.cross_boundary"
          @change="updateIntervalField('cross_boundary', $event.target.checked)"
        >
          Person's shift crosses midnight
        </a-checkbox>
      </div>

      <schedule-timeline
        title="Time"
        :intervals="interval.time_intervals"
        @add="openEditor('interval', null, interval.time_intervals.length)"
        @edit="openEditor('interval', null, $event)"
        @clear="clearIntervalTime($event)"
      />
    </div>

    <a-modal :visible="editor.visible" title="Edit Time Range" ok-text="Apply" @ok="saveEditor" @cancel="closeEditor">
      <div class="schedule-section__editor">
        <label class="schedule-section__field">
          <span>Start</span>
          <a-time-picker :value="editor.start" format="HH:mm" :minute-step="15" @change="editor.start = $event" />
        </label>
        <label class="schedule-section__field">
          <span>End</span>
          <a-time-picker :value="editor.end" format="HH:mm" :minute-step="15" @change="editor.end = $event" />
        </label>
        <a-checkbox :checked="editor.crossBoundary" @change="editor.crossBoundary = $event.target.checked">
          Time range crosses midnight
        </a-checkbox>
      </div>
    </a-modal>
  </section>
</template>

<script>
import { Checkbox, DatePicker, InputNumber, message, Modal, Radio, TimePicker } from 'ant-design-vue'
import moment from 'moment-timezone'
import ScheduleTimeline from './schedule-timeline.vue'
import {
  DAY_MINUTES,
  DEFAULT_TIME_INTERVAL,
  WEEKDAYS,
  buildInterval,
  clone,
  createInterval,
  createWeekdays,
  normalizeSection,
  timeToMinutes
} from './schedule-utils'

export default {
  components: {
    'a-checkbox': Checkbox,
    'a-date-picker': DatePicker,
    'a-input-number': InputNumber,
    'a-modal': Modal,
    'a-radio': Radio,
    'a-radio-group': Radio.Group,
    'a-time-picker': TimePicker,
    'schedule-timeline': ScheduleTimeline
  },
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      weekdays: WEEKDAYS,
      editor: {
        visible: false,
        mode: 'weekdays',
        dayKey: null,
        index: 0,
        start: moment(DEFAULT_TIME_INTERVAL.start, 'HH:mm'),
        end: moment(DEFAULT_TIME_INTERVAL.end, 'HH:mm'),
        crossBoundary: false
      }
    }
  },
  computed: {
    section() {
      return normalizeSection(this.value)
    },

    interval() {
      return createInterval(this.section.interval)
    },

    intervalStartDate() {
      return this.interval.start_date ? moment(this.interval.start_date) : null
    }
  },
  methods: {
    emitSection(section) {
      this.$emit('input', normalizeSection(section))
    },

    weekdayIntervals(dayKey) {
      return this.section.weekdays[dayKey] ?? []
    },

    getDayIndex(dayKey) {
      return WEEKDAYS.findIndex(day => day.key === dayKey)
    },

    getPreviousDayKey(dayKey) {
      const dayIndex = this.getDayIndex(dayKey)
      const previousIndex = dayIndex > 0 ? dayIndex - 1 : WEEKDAYS.length - 1

      return WEEKDAYS[previousIndex].key
    },

    isCrossBoundaryInterval(interval) {
      if (!interval?.start || !interval?.end) {
        return false
      }

      return Boolean(interval.cross_boundary) || timeToMinutes(interval.end) <= timeToMinutes(interval.start)
    },

    getSourceColorIndex(dayKey, index) {
      const dayIndex = Math.max(this.getDayIndex(dayKey), 0)

      return dayIndex * 10 + index
    },

    createDisplayInterval(interval, dayKey, index, overrides = {}) {
      return {
        ...clone(interval),
        ...overrides,
        _source: {
          dayKey,
          index
        },
        _color_index: this.getSourceColorIndex(dayKey, index),
        _disable_local_split: true
      }
    },

    displayWeekdayIntervals(dayKey) {
      const previousDayKey = this.getPreviousDayKey(dayKey)
      const carryOverIntervals = this.weekdayIntervals(previousDayKey).reduce((result, interval, index) => {
        if (!this.isCrossBoundaryInterval(interval)) {
          return result
        }

        const endMinutes = timeToMinutes(interval.end)

        if (endMinutes === 0) {
          return result
        }

        result.push(
          this.createDisplayInterval(interval, previousDayKey, index, {
            start: '00:00',
            duration_in_minutes: endMinutes,
            cross_boundary: false,
            continues_from_previous_day: true,
            _timeline_start_minutes: 0,
            _timeline_end_minutes: endMinutes
          })
        )

        return result
      }, [])

      const ownIntervals = this.weekdayIntervals(dayKey).map((interval, index) => {
        const startMinutes = timeToMinutes(interval.start)
        const endMinutes = timeToMinutes(interval.end)

        if (!this.isCrossBoundaryInterval(interval)) {
          return this.createDisplayInterval(interval, dayKey, index, {
            _timeline_start_minutes: startMinutes,
            _timeline_end_minutes: endMinutes
          })
        }

        return this.createDisplayInterval(interval, dayKey, index, {
          end: '00:00',
          duration_in_minutes: DAY_MINUTES - startMinutes,
          cross_boundary: false,
          continues_next_day: true,
          _timeline_start_minutes: startMinutes,
          _timeline_end_minutes: DAY_MINUTES
        })
      })

      return [...carryOverIntervals, ...ownIntervals]
    },

    changeType(event) {
      const selectedType = event.target.value
      const nextSection = {
        ...this.section,
        selected_type: selectedType,
        interval: selectedType === 'interval' ? createInterval(this.section.interval) : this.section.interval
      }

      this.emitSection(nextSection)
    },

    updateIntervalField(field, value) {
      const nextSection = {
        ...this.section,
        interval: {
          ...createInterval(this.section.interval),
          [field]: value
        }
      }

      this.emitSection(nextSection)
    },

    changeIntervalStartDate(date) {
      this.updateIntervalField('start_date', date ? moment(date).utc().toISOString() : null)
    },

    clearWeekday(dayKey, index) {
      const nextWeekdays = createWeekdays(this.section.weekdays)

      if (typeof index === 'number') {
        nextWeekdays[dayKey] = nextWeekdays[dayKey].filter((_, itemIndex) => itemIndex !== index)
      } else {
        nextWeekdays[dayKey] = []
      }

      this.emitSection({
        ...this.section,
        weekdays: nextWeekdays
      })
    },

    clearWeekdayPayload(payload) {
      if (payload && typeof payload === 'object') {
        this.clearWeekday(payload.dayKey, payload.index)
      }
    },

    clearIntervalTime(index) {
      const nextInterval = createInterval(this.section.interval)

      if (typeof index === 'number') {
        nextInterval.time_intervals = nextInterval.time_intervals.filter((_, itemIndex) => itemIndex !== index)
      } else {
        nextInterval.time_intervals = []
      }

      this.emitSection({
        ...this.section,
        interval: nextInterval
      })
    },

    openEditor(mode, dayKey, index) {
      const safeIndex = typeof index === 'number' ? index : 0
      const intervals = mode === 'interval' ? this.interval.time_intervals : this.weekdayIntervals(dayKey)
      const current = intervals[safeIndex] ?? DEFAULT_TIME_INTERVAL

      this.editor = {
        visible: true,
        mode,
        dayKey,
        index: safeIndex,
        start: moment(current.start, 'HH:mm'),
        end: moment(current.end, 'HH:mm'),
        crossBoundary: Boolean(current.cross_boundary)
      }
    },

    openWeekdayEditor(payload) {
      if (payload && typeof payload === 'object') {
        this.openEditor('weekdays', payload.dayKey, payload.index)
      }
    },

    closeEditor() {
      this.editor.visible = false
    },

    saveEditor() {
      if (!this.editor.start || !this.editor.end) {
        message.error('Select both start and end time.')
        return
      }

      const start = this.editor.start.format('HH:mm')
      const end = this.editor.end.format('HH:mm')
      const startMinutes = timeToMinutes(start)
      const endMinutes = timeToMinutes(end)

      if (endMinutes <= startMinutes && !this.editor.crossBoundary) {
        message.error('End time must be after start time or cross midnight.')
        return
      }

      const nextInterval = buildInterval(start, end, this.editor.crossBoundary)

      if (this.editor.mode === 'interval') {
        this.saveIntervalEditor(nextInterval)
      } else {
        this.saveWeekdayEditor(nextInterval)
      }

      this.closeEditor()
    },

    saveWeekdayEditor(nextInterval) {
      const nextWeekdays = createWeekdays(this.section.weekdays)
      const nextIntervals = [...(nextWeekdays[this.editor.dayKey] ?? [])]

      nextIntervals.splice(this.editor.index, 1, nextInterval)
      nextWeekdays[this.editor.dayKey] = nextIntervals

      this.emitSection({
        ...this.section,
        weekdays: nextWeekdays
      })
    },

    saveIntervalEditor(nextInterval) {
      const interval = createInterval(this.section.interval)
      const nextIntervals = [...interval.time_intervals]

      nextIntervals.splice(this.editor.index, 1, nextInterval)

      this.emitSection({
        ...this.section,
        interval: {
          ...interval,
          time_intervals: nextIntervals
        }
      })
    }
  }
}
</script>

<style scoped>
.schedule-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.schedule-section__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.schedule-section__title {
  margin: 0;
  color: var(--heading-color);
  font-size: 20px;
  font-weight: 700;
}

.schedule-section__type {
  display: flex;
  gap: 12px;
  background: transparent;
}

.schedule-section__days {
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.schedule-section__interval {
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.schedule-section__interval-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 16px 24px;
  align-items: end;
}

.schedule-section__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--text-color-alpha8);
  font-weight: 600;
}

.schedule-section__field .ant-input-number,
.schedule-section__field .ant-calendar-picker,
.schedule-section__field .ant-time-picker {
  width: 100%;
}

.schedule-section__date {
  width: 100%;
}

.schedule-section__checkbox {
  align-self: center;
  color: var(--text-color-alpha8);
  font-weight: 600;
}

.schedule-section__editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.schedule-section__editor .ant-checkbox-wrapper {
  grid-column: 1 / -1;
}

@media (max-width: 768px) {
  .schedule-section__interval-fields,
  .schedule-section__editor {
    grid-template-columns: 1fr;
  }
}
</style>
